// src/components/paginas/OrdenesHistorial.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_BASE;
  if (env && env.trim()) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase();

const fetchJSON = async (url, opts = {}) => {
  const resp = await fetch(url, { credentials: 'include', ...opts });
  let j = null;
  try { j = await resp.json(); } catch {}
  if (!resp.ok || (j && j.ok === false)) {
    const msg = (j && j.msg) || resp.statusText || `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return j ?? {};
};

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'Pendiente', label: 'Pendiente' },
  { value: 'enProceso', label: 'En Proceso' },
  { value: 'enRuta', label: 'En Ruta' },
  { value: 'Entregado', label: 'Entregado' },
  { value: 'Cancelado', label: 'Cancelado' },
];

export default function OrdenesHistorial() {
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const acRef = useRef(null);

  const runSearch = useCallback(async (p=1) => {
    setLoading(true);
    setErr('');
    if (acRef.current) acRef.current.abort();
    const ac = new AbortController();
    acRef.current = ac;
    try {
      const params = new URLSearchParams({ page: String(p), pageSize: String(pageSize) });
      if (q.trim()) params.set('q', q.trim());
      if (estado) params.set('estado', estado);
      if (from) params.set('from', from);
      if (to) params.set('to', to);

      const j = await fetchJSON(`${API_BASE}/api/web/pedidos/historial?${params.toString()}`, { signal: ac.signal });
      setRows(j.data || []);
      setTotal(j.total || 0);
      setPage(j.page || p);
    } catch (e) {
      if (e.name !== 'AbortError') setErr(e.message || 'Error al buscar');
    } finally {
      setLoading(false);
    }
  }, [q, estado, from, to, pageSize]);

  // Ejecutar sólo al montar; mantenemos el buscador manual en el botón "Buscar"
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runSearch(1); }, []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / Math.max(1, pageSize))), [total, pageSize]);

  const onSubmit = (e) => { e.preventDefault(); runSearch(1); };

  return (
    <div className="p-4 sm:p-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Historial de pedidos</h1>
          <p className="text-sm text-gray-600">Busca pedidos por cliente, boleta, banco, WhatsApp, NIT, estado o fecha.</p>
        </div>
        <button className="px-4 py-2 rounded-xl bg-sky-600 text-white hover:bg-sky-700" onClick={() => runSearch(page)} disabled={loading}>
          {loading ? 'Buscando…' : 'Actualizar'}
        </button>
      </header>

      <form onSubmit={onSubmit} className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-3 bg-white border rounded-2xl p-4">
        <input className="border rounded-lg px-3 py-2" placeholder="Buscar texto…" value={q} onChange={e => setQ(e.target.value)} />
        <select className="border rounded-lg px-3 py-2 bg-white" value={estado} onChange={e => setEstado(e.target.value)}>
          {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>
        <input type="date" className="border rounded-lg px-3 py-2" value={from} onChange={e => setFrom(e.target.value)} />
        <input type="date" className="border rounded-lg px-3 py-2" value={to} onChange={e => setTo(e.target.value)} />
        <div className="flex gap-2">
          <button className="px-4 py-2 rounded-xl bg-sky-600 text-white hover:bg-sky-700" disabled={loading}>
            {loading ? 'Buscando…' : 'Buscar'}
          </button>
          <select className="border rounded-lg px-2" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
            {[20, 50, 100, 200].map(n => <option key={n} value={n}>{n}/pág.</option>)}
          </select>
        </div>
      </form>

      {!!err && (
        <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>
      )}
      <div className="mt-4 rounded-2xl border bg-white shadow-sm overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              {['ID','Cliente','Boleta','Banco','WhatsApp','NIT','Total','Estado','Fecha','Acciones'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-2">{r.id}</td>
                <td className="px-4 py-2">{r.cliente}</td>
                <td className="px-4 py-2">{r.boleta || '-'}</td>
                <td className="px-4 py-2">{r.banco || '-'}</td>
                <td className="px-4 py-2">{r.whatsapp || '-'}</td>
                <td className="px-4 py-2">{r.nit || '-'}</td>
                <td className="px-4 py-2">Q{Number(r.total || 0).toFixed(2)}</td>
                <td className="px-4 py-2">{r.estado}</td>
                <td className="px-4 py-2">{new Date(r.fecha).toLocaleString()}</td>
                <td className="px-4 py-2">
                  <Link className="text-sky-700 hover:underline" to={`/admin/ordenes-web/${r.id}`}>Ver</Link>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={10}>
                  {loading ? 'Cargando…' : 'Sin resultados'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">Total: {total} • Página {page} de {totalPages}</div>
        <div className="flex gap-2">
          <button className="px-3 py-2 border rounded-lg" onClick={() => { if (page > 1) runSearch(page - 1); }} disabled={page <= 1 || loading}>
            ‹ Anterior
          </button>
          <button className="px-3 py-2 border rounded-lg" onClick={() => { if (page < totalPages) runSearch(page + 1); }} disabled={page >= totalPages || loading}>
            Siguiente ›
          </button>
        </div>
      </div>
    </div>
  );
}
